import { store } from '@/store';
import axios from '@/utils/axios';
import router from '@/router';
import browser from '@/utils/browser';
import Worker from '@/utils/timer.worker';
import moment from 'moment';

const auth = {
    worker: null,
    mousemoveEvent: false,
    idleTimeout: Math.floor(process.env.VUE_APP_IDLE_TIMEOUT / 60),
    /**
     * 背景運作檢查並更新 token ，出錯時無需反饋
     * 只有在已登入並且 jwt token 過期，refresh token 還未過期時需要 call refresh api
     * 如果 jwt token 和 refresh token 二個皆過期，跳出 token 過期提醒視窗並登出
     * 先檢查是否已經有 refresh api 在運作中
     */
    passiveRefreshToken() {
        if (!store.state.auth.token || store.state.auth.refreshProcessing)
            return;

        if (
            store.getters['auth/expired'] &&
            store.getters['auth/refreshable']
        ) {
            this.postRefreshToken().then().catch(() => {}); //避免出現uncaught in promise error
        } else if (store.getters['auth/expired']) {
            store.commit('setDialog', {
                type: 'remind',
                title: '登入時效已到期',
                text: '請重新登入會員，即可繼續使用會員相關服務或投保繳費。',
                active: true,
            });
            auth.logout();
        }
    },
    /**
     * 主動檢查 token 狀態
     * (在需要有效token的操作時，如 call 有 auth 的 api 前，使用這個 function 檢查 token)，如檢查失敗應該要中斷 user 操作
     * 只有在已登入並且 jwt token 過期，refresh token 還未過期時需要 call refresh api
     * 如果 jwt token 和 refresh token 二個皆過期，跳出 token 過期提醒視窗並登出
     * 先檢查是否已經有 refresh api 在運作中
     */
    async checkToken() {
        return new Promise((resolve) => {
            if (!store.state.auth.token) {
                // 沒有 token (未登入過)直接中斷
                resolve();
            } else if (store.state.auth.refreshProcessing) {
                // 如果 refresh api 已送出仍在處理中，直接 return
                resolve();
            } else if (!store.getters['auth/refreshable']) {
                // 如果 refresh token 過期

                store.commit('setTimeout');
                auth.logout();
                resolve();
            } else if (
                store.getters['auth/expired'] &&
                store.getters['auth/refreshable']
            ) {
                // 如果 jwt token 過期， refresh token 未過期， call refresh api
                this.postRefreshToken()
                    .then(() => {
                        resolve(true);
                    })
                    .catch(() => {
                        store.commit('setDialog', {
                            type: 'remind',
                            title: '登入時效已到期',
                            text: '請重新登入會員，即可繼續使用會員相關服務或投保繳費。',
                            active: true,
                        });

                        auth.logout();
                        resolve();
                    });
            } else {
                // 未過期
                resolve();
            }
        });
    },
    /**
     * token 倒數
     * login submit 進入點
     */
    tokenCountdown() {
        if (this.worker) {
            // 判斷是否有 refresh token (第一次登入時會建立 worker)
            this.worker.terminate();
        }

        this.mousemoveEvent = false;

        this.worker = new Worker();

        this.worker.postMessage(store.state.auth.refreshTokenExpireSec);

        // loop event
        this.worker.onmessage = () => {
            store.commit('auth/countdown');
            // 判斷 refresh token 是否過期
            if (!store.state.auth.refreshTokenExpireSec) {
                this.worker.terminate();
                store.commit('setTimeout');
                auth.logout();
                return;
            }

            // 判斷 jwt token
            if (!store.state.auth.jwtExpireSec && !this.mousemoveEvent) {
                window.addEventListener('mousemove', auth.refreshToken);
                window.addEventListener('touchstart', auth.refreshToken);
                this.mousemoveEvent = true;
            }
        };
    },
    /**
     * 登出
     * 載入 API-Logout 的資料
     */
    logout() {
        if (this.worker) this.worker.terminate();
        window.removeEventListener('mousemove', auth.refreshToken);
        window.removeEventListener('touchstart', auth.refreshToken);
        window.removeEventListener('beforeunload', browser.leavePageAlert);
        this.mousemoveEvent = false;
        store.commit('setAllowLeavePage', true);
        
        axios
            .get(apiUrl.Logout, {})
            .finally(() => {
                store.commit('auth/logout');
                store.commit('member/clear');
                store.dispatch('reset');
                localStorage.removeItem('jwtInfo');
                return router.push(`/${router.currentRoute.value.params.type}`);
            });
    },
    /**
     * 申請新的token
     */
    refreshToken() {
        window.removeEventListener('mousemove', auth.refreshToken);
        window.removeEventListener('touchstart', auth.refreshToken);

        // 如果 refresh token 過期或是從沒登入過，直接直出並 return
        if (!store.getters['auth/refreshable']) {
            store.commit('setDialog', {
                type: 'remind',
                title: '登入時效已到期',
                text: '請重新登入會員，即可繼續使用會員相關服務或投保繳費。',
                active: true,
            });

            auth.logout();

            return;
        }

        // 如果 refresh api 已送出仍在處理中，直接 return
        if (store.state.auth.refreshProcessing) return;

        store.commit('auth/setRefreshProcessing', true);

        auth.postRefreshToken()
            .then(() => {
                return true;
            })
            .catch(() => {
                store.commit('setDialog', {
                    type: 'remind',
                    title: '登入時效已到期',
                    text: '請重新登入會員，即可繼續使用會員相關服務或投保繳費。',
                    active: true,
                });
    
                auth.logout();
                return false;
            });
    },
    /**
     * 向 API-RefreshToken4WT 發送POST請求，要求token資料
     * @returns
     */
    postRefreshToken() {
        store.commit('auth/setRefreshProcessing', true);
        return new Promise((resolve, reject) => {
            // 提醒： 測試站用 post， git push 時要切換回 get
            // return axios.post(apiUrl.RefreshToken4WT, {
            //     account: store.state.auth.account,
            //     token: store.state.auth.refreshToken
            // })
            return axios
                .get(apiUrl.RefreshToken)
                .then((res) => {
                    if (res.data.code === 200) {
                        store.commit('auth/login', res.data.data);
                        auth.tokenCountdown();
                        resolve();
                    }
                    reject(res.data);
                })
                .catch((err) => {
                    reject(err.response.data);
                })
                .then(() => {
                    store.commit('auth/setRefreshProcessing', false);
                });
        });
    },
    /**
     * 向 API-Userapi 取得用戶資料
     * @returns promise
     */
    getUser(){
        return axios.get(apiUrl.Userapi, {
            headers: {'Auth': store.state.auth.token}
        }).then(res => {
            if (res.data.code !== 200) {

                store.commit('setDialog', {
                    active: true,
                    text: res.data.message,
                });

                return;
            }

            store.commit('member/setId', res.data.data.applicantIdNo);
            store.commit('member/setEmail', res.data.data.applicantEmail);
            store.commit('member/setBirthday', res.data.data.applicantBirthday);
            store.commit('member/setName', res.data.data.applicantName);
            store.commit('member/setMobile', res.data.data.applicantMobile);
            store.commit('member/setZipCode', res.data.data.applicantZipCode);
            store.commit('member/setCounty', res.data.data.applicantCityName);
            store.commit('member/setDistrict', res.data.data.applicantZipName);
            store.commit('member/setAddress', res.data.data.applicantAddress); 
        }).catch(err => {
            reject(err);
        });
    },
    /**
     * 將jwt token放入localStorage
     */
    setJwtInfo(token){
        //先更新jwtInfo裡面的jwtExpire再存入localStorage
        token.jwtExpire = store.state.jwtExpire;
        //localStorage裡面不要出現個資
        token.account = '';
        localStorage.setItem('jwtInfo', JSON.stringify(token));
    },
    /**
     * 將localStorage的jwtInfo清除
     */
    removeJwtInfo(){
        localStorage.removeItem('jwtInfo');
    },
};

export default auth;
